import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Group,
  Indicator,
  Menu,
  Popover,
  SegmentedControl,
  Stack,
  TextInput,
} from '@mantine/core';
import { DatePicker, DatePickerInput, DatesRangeValue } from '@mantine/dates';
import {
  IoSearchOutline,
  IoCalendarOutline,
  IoChevronDown,
  IoChevronUp,
  IoMailOpenOutline,
  IoMailUnreadOutline,
} from 'react-icons/io5';
import {
  InboxChatModeEnum,
  InboxQueryParamsEnum,
} from '@th-types/inbox-reviews.type';
import { toDate } from '@utils/DateUtils';

interface InboxFilterBarProps {
  onSearch: (params: {
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }) => void;
  onClear: () => void;
  onToggleUnread?: () => void;
  showOnlyUnread?: boolean;
  chatMode?: InboxChatModeEnum | null;
  onChatModeChange?: (mode: InboxChatModeEnum | null) => void;
  onMarkAllAsRead?: () => void;
  onMarkAllAsUnread?: () => void;
  selectedCount?: number;
  onMarkSelectionAsRead?: () => void;
  onMarkSelectionAsUnread?: () => void;
}

export default function InboxFilterBar({
  onSearch,
  onClear,
  onToggleUnread,
  showOnlyUnread,
  chatMode,
  onChatModeChange,
  onMarkAllAsRead,
  onMarkAllAsUnread,
  selectedCount = 0,
  onMarkSelectionAsRead,
  onMarkSelectionAsUnread,
}: InboxFilterBarProps) {
  const [searchParams] = useSearchParams();

  const [searchText, setSearchText] = useState(
    () => searchParams.get(InboxQueryParamsEnum.SEARCH_TEXT) || ''
  );
  const [dateRange, setDateRange] = useState<DatesRangeValue>(() => {
    const from = searchParams.get(InboxQueryParamsEnum.DATE_FROM);
    const to = searchParams.get(InboxQueryParamsEnum.DATE_TO);
    return [from ? new Date(from) : null, to ? new Date(to) : null];
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);

  const handleSearch = () => {
    onSearch({
      searchText,
      dateRange: {
        startDate: toDate(dateRange[0]),
        endDate: toDate(dateRange[1]),
      },
    });
  };

  const handleClear = () => {
    setSearchText('');
    setDateRange([null, null]);
    onClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const isMessagingTab = !!onChatModeChange;

  const moreMenu = (onMarkAllAsRead || onMarkAllAsUnread) && (
    <Menu position="bottom-end" opened={isMenuOpen} onChange={setIsMenuOpen}>
      <Menu.Target>
        <Button
          variant="light"
          color="gray"
          leftSection={
            isMenuOpen ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />
          }
          fullWidth={isMobile}
        >
          More
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {onMarkAllAsRead && (
          <Menu.Item
            leftSection={<IoMailOpenOutline size={16} />}
            onClick={onMarkAllAsRead}
          >
            Mark all as Read
          </Menu.Item>
        )}
        {onMarkAllAsUnread && (
          <Menu.Item
            leftSection={<IoMailUnreadOutline size={16} />}
            onClick={onMarkAllAsUnread}
          >
            Mark all as Unread
          </Menu.Item>
        )}
        {selectedCount > 0 && (
          <>
            <Menu.Divider />
            {onMarkSelectionAsRead && (
              <Menu.Item
                leftSection={<IoMailOpenOutline size={16} />}
                onClick={onMarkSelectionAsRead}
              >
                Mark selection as Read ({selectedCount})
              </Menu.Item>
            )}
            {onMarkSelectionAsUnread && (
              <Menu.Item
                leftSection={<IoMailUnreadOutline size={16} />}
                onClick={onMarkSelectionAsUnread}
              >
                Mark selection as Unread ({selectedCount})
              </Menu.Item>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  if (isMobile) {
    return (
      <Stack gap="sm" mb="md">
        {onChatModeChange && (
          <SegmentedControl
            size="md"
            fullWidth
            value={chatMode ?? 'all'}
            onChange={(value) =>
              onChatModeChange(
                value === 'all' ? null : (value as InboxChatModeEnum)
              )
            }
            data={[
              { label: 'Inbox', value: InboxChatModeEnum.INBOX },
              { label: 'Sent', value: InboxChatModeEnum.SENT },
              { label: 'All', value: 'all' },
            ]}
          />
        )}

        <Group gap="xs" wrap="nowrap">
          <TextInput
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            leftSection={<IoSearchOutline size={16} />}
            rightSectionPointerEvents="all"
            rightSection={
              searchText ? (
                <CloseButton size="sm" onClick={() => setSearchText('')} />
              ) : undefined
            }
            rightSectionWidth={36}
            style={{ flex: 1 }}
          />
          <Popover
            opened={isDatePopoverOpen}
            onChange={setIsDatePopoverOpen}
            position="bottom-end"
            withArrow
            shadow="md"
          >
            <Popover.Target>
              <Indicator
                disabled={!dateRange[0] && !dateRange[1]}
                color="yellow"
                size={10}
                offset={4}
              >
                <ActionIcon
                  size="lg"
                  variant="default"
                  aria-label="Open date range picker"
                  onClick={() => setIsDatePopoverOpen((prev) => !prev)}
                >
                  <IoCalendarOutline size={18} />
                </ActionIcon>
              </Indicator>
            </Popover.Target>
            <Popover.Dropdown p="sm">
              <Stack gap="sm" align="center">
                <DatePicker
                  type="range"
                  allowSingleDateInRange
                  value={dateRange}
                  onChange={setDateRange}
                />
                <Group gap="xs" justify="center" w="100%">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => setDateRange([null, null])}
                    disabled={!dateRange[0] && !dateRange[1]}
                  >
                    Clear
                  </Button>
                  <Button
                    size="xs"
                    variant="filled"
                    onClick={() => setIsDatePopoverOpen(false)}
                  >
                    Done
                  </Button>
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
          <ActionIcon
            size="lg"
            variant="filled"
            onClick={handleSearch}
            aria-label="Search"
            style={{
              backgroundColor: 'var(--yellow)',
              color: 'var(--black)',
            }}
          >
            <IoSearchOutline size={18} />
          </ActionIcon>
        </Group>

        {isMessagingTab && onToggleUnread && (
          <Box
            p="sm"
            bg="var(--anti-flash-white)"
            style={{ borderRadius: '4px' }}
          >
            <Checkbox
              label="Unread"
              checked={showOnlyUnread ?? false}
              onChange={onToggleUnread}
            />
          </Box>
        )}

        {moreMenu}
      </Stack>
    );
  }

  return (
    <Flex
      gap="sm"
      align="center"
      wrap="wrap"
      mb="md"
      justify={isMessagingTab ? undefined : 'flex-end'}
    >
      {onChatModeChange && (
        <SegmentedControl
          size="md"
          value={chatMode ?? 'all'}
          onChange={(value) =>
            onChatModeChange(
              value === 'all' ? null : (value as InboxChatModeEnum)
            )
          }
          data={[
            { label: 'Inbox', value: InboxChatModeEnum.INBOX },
            { label: 'Sent', value: InboxChatModeEnum.SENT },
            { label: 'All', value: 'all' },
          ]}
        />
      )}

      {isMessagingTab && onToggleUnread && (
        <Box
          p="xs"
          ml="auto"
          bg="var(--anti-flash-white)"
          style={{ borderRadius: '4px' }}
        >
          <Checkbox
            label="Unread"
            checked={showOnlyUnread ?? false}
            onChange={onToggleUnread}
          />
        </Box>
      )}

      <TextInput
        placeholder="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        leftSection={<IoSearchOutline size={16} />}
        rightSectionPointerEvents="all"
        rightSection={
          searchText ? (
            <CloseButton size="sm" onClick={() => setSearchText('')} />
          ) : undefined
        }
        rightSectionWidth={36}
        w="384px"
      />

      <DatePickerInput
        type="range"
        placeholder="Select date range"
        allowSingleDateInRange
        value={dateRange}
        onChange={setDateRange}
        clearable
        miw="220px"
      />

      <Group gap="xs">
        <Button size="sm" onClick={handleSearch}>
          Search
        </Button>
        <Button size="sm" variant="light" onClick={handleClear}>
          Clear
        </Button>
      </Group>

      {moreMenu}
    </Flex>
  );
}
